import React from 'react'

import { createGenericContext } from '../../../_utils/create-generic-context'
import { IRuleConfigurationReturnType, IRuleTemplateReturnType } from '../data'
import { ActionTypesUnion, ConditionsSelection, EntityTypeEnum, IConditions, TriggerTypesUnion } from '../types'
import { defaultEntityType } from '../utils'

// Interface for the context value
interface CopilotContextValue {
  handleRuleDescriptionChange: (ruleDescription: string) => void
  handleRuleEntityTypeChange: (entityType: EntityTypeEnum) => void
  handleRuleNameChange: (ruleName: string) => void
  handleRuleTagsChange: (tags: string[]) => void
  handleSetSelectedActions: (actions: ActionTypesUnion[] | null) => void
  handleSetSelectedCondition: (condition: ConditionsSelection | null) => void
  handleSetSelectedConditions: (conditions: IConditions | null) => void
  handleSetSelectedRule: (rule: Partial<IRuleTemplateReturnType> | Partial<IRuleConfigurationReturnType> | null) => void
  handleSetSelectedTrigger: (trigger: TriggerTypesUnion | null) => void
  selectedCondition: ConditionsSelection | null
  selectedRule: Partial<IRuleTemplateReturnType> | Partial<IRuleConfigurationReturnType>
}

const [useCopilotContext, CopilotContextProvider] = createGenericContext<CopilotContextValue>(
  'useColorModeContext must be used within the ColorModeContextProvider'
)

// Create the provider
const CopilotProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [selectedRule, setSelectedRule] = React.useState<
    Partial<IRuleTemplateReturnType> | Partial<IRuleConfigurationReturnType>
  >({ entityType: defaultEntityType })
  const [selectedCondition, setSelectedCondition] = React.useState<ConditionsSelection | null>(null)

  const value: CopilotContextValue = {
    handleRuleDescriptionChange: (ruleDescription) => {
      setSelectedRule({ ...selectedRule, description: ruleDescription })
    },
    handleRuleEntityTypeChange: (entityType: EntityTypeEnum) => {
      setSelectedRule({
        ...selectedRule,
        actions: undefined,
        conditions: undefined,
        entityType: entityType,
        trigger: undefined
      })
    },
    handleRuleNameChange: (ruleName) => {
      setSelectedRule({ ...selectedRule, name: ruleName })
    },
    handleRuleTagsChange: (tags) => {
      // Ensure all tags are uppercase
      // If tags is undefined or null, use an empty array
      const uppercaseTags = tags ? tags.map((tag) => tag.toUpperCase()) : []
      setSelectedRule({ ...selectedRule, tags: uppercaseTags })
    },
    handleSetSelectedActions: (actions) => {
      setSelectedRule({ ...selectedRule, actions: actions || undefined })
    },
    handleSetSelectedCondition: (condition) => {
      setSelectedCondition(condition)
    },
    handleSetSelectedConditions: (conditions) => {
      setSelectedRule({ ...selectedRule, conditions: conditions || undefined })
    },
    handleSetSelectedRule: (rule) => {
      setSelectedRule(rule || { entityType: defaultEntityType })
    },
    handleSetSelectedTrigger: (trigger) => {
      setSelectedRule({ ...selectedRule, trigger: trigger || undefined })
    },
    selectedCondition: selectedCondition,
    selectedRule: selectedRule
  }

  return <CopilotContextProvider value={value}>{children}</CopilotContextProvider>
}

export { CopilotProvider, useCopilotContext }
